import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { 
 Search, 
 BookOpen, 
 FileText, 
 Users,
 Calendar,
 Quote,
 ExternalLink,
 Filter,
 SortDesc,
 Clock,
 Star,
 Download,
 Copy,
 ArrowLeft,
 AlertCircle,
 X,
 RotateCcw,
 TrendingUp,
 Globe,
 Hash,
 UserCheck,
 Zap
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import scholarAPI from '../api/scholarApi';

export default function GoogleScholarPage() {
 const { t, i18n } = useTranslation();
 const navigate = useNavigate();
 const { isLoggedIn, user } = useSelector((state) => state.user);
 
 // 搜索相关状态
 const [searchQuery, setSearchQuery] = useState('');
 const [searchResults, setSearchResults] = useState([]);
 const [loading, setLoading] = useState(false);
 const [totalResults, setTotalResults] = useState(0);
 const [currentPage, setCurrentPage] = useState(1);
 const [error, setError] = useState('');
 
 // 筛选和排序状态
 const [filters, setFilters] = useState({
 yearFrom: '',
 yearTo: '',
 sortBy: 'relevance', // relevance, date, cited
 language: 'any',
 citationMin: '', // 最小引用数
 venue: '', // 期刊/会议名称
 author: '' // 作者姓名
 });
 
 const [showFilters, setShowFilters] = useState(true);

 // 计算活跃筛选数量
 const getActiveFiltersCount = () => {
 let count = 0;
 if (filters.yearFrom) count++;
 if (filters.yearTo) count++;
 if (filters.sortBy !== 'relevance') count++;
 if (filters.language !== 'any') count++;
 if (filters.citationMin) count++;
 if (filters.venue) count++;
 if (filters.author) count++;
 return count;
 };

 // 清除所有筛选
 const clearAllFilters = () => {
 setFilters({
 yearFrom: '',
 yearTo: '',
 sortBy: 'relevance',
 language: 'any',
 citationMin: '',
 venue: '',
 author: ''
 });
 };

 // 预设筛选选项
 const presetFilters = [
 { name: '近5年', action: () => setFilters({...filters, yearFrom: '2019', yearTo: '2024'}) },
 { name: '高引用', action: () => setFilters({...filters, citationMin: '100', sortBy: 'cited'}) },
 { name: '最新优先', action: () => setFilters({...filters, sortBy: 'date'}) },
 { name: '英文文献', action: () => setFilters({...filters, language: 'en'}) }
 ];

 // 处理搜索
 const handleSearch = async (query = searchQuery, page = 1) => {
 // 如果第一个参数是事件对象，使用默认的searchQuery
 if (query && typeof query === 'object' && query.type) {
 query = searchQuery;
 }
 
 // 确保query是字符串类型
 if (typeof query !== 'string' || !query.trim()) return;
 
 setLoading(true);
 setError('');
 
 try {
 console.log('开始搜索:', { query, page, filters });
 
 // 调用真实的API
 const response = await scholarAPI.search(query, filters, page);
 
 console.log('搜索结果:', response);
 
 setSearchResults(response.results);
 setTotalResults(response.totalResults);
 setCurrentPage(response.currentPage);
 
 } catch (error) {
 console.error('搜索失败:', error);
 setError(error.message || '搜索失败，请稍后重试');
 setSearchResults([]);
 setTotalResults(0);
 } finally {
 setLoading(false);
 }
 };

 // 处理回车搜索
 const handleKeyPress = (e) => {
 if (e.key === 'Enter') {
 handleSearch();
 }
 };

 // 渲染搜索结果项
 const renderSearchResult = (result) => {
 return (
 <div key={result.id} className="bg-white rounded-lg border border-ink-200 p-4 sm:p-6 hover:shadow-md transition-all duration-300">
 {/* 标题 */}
 <div className="mb-3">
 <a
 href={result.url}
 target="_blank"
 rel="noopener noreferrer"
 className="text-lg font-semibold text-accent-600 hover:text-accent-700 hover:underline leading-tight"
 >
 {result.title}
 </a>
 </div>

 {/* 作者和发表信息 */}
 <div className="flex flex-wrap items-center gap-2 mb-3 text-sm text-ink-600">
 <div className="flex items-center gap-1">
 <Users className="w-4 h-4" />
 <span>{result.authors.join(', ')}</span>
 </div>
 <span>•</span>
 <div className="flex items-center gap-1">
 <Calendar className="w-4 h-4" />
 <span>{result.year}</span>
 </div>
 {result.venue && (
 <>
 <span>•</span>
 <span className="text-ink-500">{result.venue}</span>
 </>
 )}
 </div>

 {/* 摘要 */}
 <p className="text-ink-700 text-sm leading-relaxed mb-4 line-clamp-3">
 {result.abstract}
 </p>

 {/* 底部操作栏 */}
 <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-ink-100">
 <div className="flex items-center gap-4 text-sm text-ink-600">
 {/* 引用数 */}
 <div className="flex items-center gap-1">
 <Quote className="w-4 h-4" />
 <span>{result.citations.toLocaleString()} 次引用</span>
 </div>
 </div>

 <div className="flex items-center gap-2">
 {/* PDF下载 */}
 {result.pdfUrl && (
 <button
 onClick={() => window.open(result.pdfUrl, '_blank')}
 className="flex items-center gap-1 px-3 py-1.5 text-sm text-ink-600 hover:text-accent-600 hover:bg-accent-50 rounded-md transition-colors duration-300"
 >
 <Download className="w-4 h-4" />
 <span>PDF</span>
 </button>
 )}
 
 {/* 复制引用 */}
 <button className="flex items-center gap-1 px-3 py-1.5 text-sm text-ink-600 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors duration-300">
 <Copy className="w-4 h-4" />
 <span>引用</span>
 </button>
 
 {/* 查看详情 */}
 <button
 onClick={() => window.open(result.url, '_blank')}
 className="flex items-center gap-1 px-3 py-1.5 text-sm bg-accent-50 text-accent-600 hover:bg-accent-100 rounded-md transition-colors duration-300"
 >
 <ExternalLink className="w-4 h-4" />
 <span>查看</span>
 </button>
 </div>
 </div>
 </div>
 );
 };

 return (
 <div className="min-h-screen bg-cream-100">
 <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8">
 
 {/* 页面标题和返回按钮 */}
 <div className="flex items-center gap-4 mb-6 sm:mb-8">
 <button
 onClick={() => navigate('/ai-toolbox/sidebar')}
 className="flex items-center gap-2 px-4 py-2 text-ink-600 hover:text-ink-900 hover:bg-cream-100 rounded-lg transition-colors duration-300"
 >
 <ArrowLeft className="w-4 h-4" />
 <span className="hidden sm:inline">{t('googleScholar.backToToolbox')}</span>
 </button>
 
 <div className="flex items-center gap-3">
 <div className="w-10 h-10 sm:w-12 sm:h-12 bg-accent-100 rounded-lg flex items-center justify-center">
 <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-accent-600" />
 </div>
 <div>
 <h1 className="text-2xl sm:text-3xl font-bold text-ink-900">{t('googleScholar.title')}</h1>
 <p className="text-sm sm:text-base text-ink-600">{t('googleScholar.description')}</p>
 </div>
 </div>
 </div>

 {/* 搜索栏 */}
 <div className="bg-white rounded-lg sm:rounded-xl border border-ink-200 shadow-sm p-4 sm:p-6 mb-6 sm:mb-8">
 <div className="flex flex-col lg:flex-row gap-4">
 {/* 主搜索框 */}
 <div className="flex-1">
 <div className="relative">
 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-ink-400" />
 <input
 type="text"
 placeholder={t('googleScholar.search.placeholder')}
 value={searchQuery}
 onChange={(e) => setSearchQuery(e.target.value)}
 onKeyPress={handleKeyPress}
 className="w-full pl-10 pr-4 py-3 border border-ink-200 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent text-base"
 />
 </div>
 </div>

 {/* 操作按钮组 */}
 <div className="flex gap-2">
 <button
 onClick={handleSearch}
 disabled={loading || !searchQuery.trim()}
 className="px-6 py-3 bg-accent-500 text-white rounded-lg font-medium hover:bg-accent-600 disabled:bg-ink-200 disabled:cursor-not-allowed transition-colors duration-300 flex items-center gap-2"
 >
 {loading ? (
 <>
 <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
 <span>搜索中</span>
 </>
 ) : (
 <>
 <Search className="w-4 h-4" />
 <span>搜索</span>
 </>
 )}
 </button>
 
 <button
 onClick={() => setShowFilters(!showFilters)}
 className={`relative px-4 py-3 border rounded-lg transition-all duration-300 flex items-center gap-2 ${
 getActiveFiltersCount() > 0 
 ? 'border-accent-500 bg-accent-50 text-accent-600' 
 : 'border-ink-200 text-ink-600 hover:bg-cream-100'
 }`}
 >
 <Filter className="w-4 h-4" />
 <span className="hidden sm:inline">筛选</span>
 {getActiveFiltersCount() > 0 && (
 <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent-500 text-white text-xs rounded-full flex items-center justify-center">
 {getActiveFiltersCount()}
 </span>
 )}
 </button>
 </div>
 </div>

 {/* 高级筛选面板 */}
 {showFilters && (
 <div className="mt-6 p-6 bg-cream-50 rounded-xl border border-ink-200/50 shadow-sm">
 {/* 筛选标题和操作 */}
 <div className="flex items-center justify-between mb-6">
 <div className="flex items-center gap-3">
 <div className="w-8 h-8 bg-accent-100 rounded-lg flex items-center justify-center">
 <Filter className="w-4 h-4 text-accent-600" />
 </div>
 <div>
 <h3 className="text-lg font-semibold text-ink-900">高级筛选</h3>
 <p className="text-sm text-ink-600">精准筛选，找到最相关的文献</p>
 </div>
 </div>
 <div className="flex items-center gap-2">
 {getActiveFiltersCount() > 0 && (
 <button
 onClick={clearAllFilters}
 className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
 >
 <RotateCcw className="w-3 h-3" />
 清除全部
 </button>
 )}
 <button
 onClick={() => setShowFilters(false)}
 className="p-1 text-ink-400 hover:text-ink-600 hover:bg-cream-100 rounded-md transition-colors"
 >
 <X className="w-4 h-4" />
 </button>
 </div>
 </div>

 {/* 预设筛选快捷按钮 */}
 <div className="mb-6">
 <h4 className="text-sm font-medium text-ink-700 mb-3 flex items-center gap-2">
 <Zap className="w-4 h-4 text-accent-500" />
 快速筛选
 </h4>
 <div className="flex flex-wrap gap-2">
 {presetFilters.map((preset, index) => (
 <button
 key={index}
 onClick={preset.action}
 className="px-3 py-1.5 text-sm bg-cream-50 border border-ink-200 text-ink-700 hover:bg-accent-50 hover:border-accent-300 hover:text-accent-700 rounded-lg transition-all duration-200"
 >
 {preset.name}
 </button>
 ))}
 </div>
 </div>

 {/* 筛选表单 */}
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
 {/* 时间范围 */}
 <div className="space-y-4">
 <h4 className="text-sm font-medium text-ink-700 flex items-center gap-2">
 <Calendar className="w-4 h-4 text-accent-500" />
 发表时间
 </h4>
 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="block text-xs text-ink-500 mb-1">起始年份</label>
 <input
 type="number"
 placeholder="如: 2020"
 value={filters.yearFrom}
 onChange={(e) => setFilters({...filters, yearFrom: e.target.value})}
 className="w-full px-3 py-2 text-sm border border-ink-200 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-all"
 />
 </div>
 <div>
 <label className="block text-xs text-ink-500 mb-1">结束年份</label>
 <input
 type="number"
 placeholder="如: 2024"
 value={filters.yearTo}
 onChange={(e) => setFilters({...filters, yearTo: e.target.value})}
 className="w-full px-3 py-2 text-sm border border-ink-200 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-all"
 />
 </div>
 </div>
 </div>

 {/* 排序和语言 */}
 <div className="space-y-4">
 <h4 className="text-sm font-medium text-ink-700 flex items-center gap-2">
 <SortDesc className="w-4 h-4 text-green-500" />
 排序与语言
 </h4>
 <div className="space-y-3">
 <div>
 <label className="block text-xs text-ink-500 mb-1">排序方式</label>
 <select
 value={filters.sortBy}
 onChange={(e) => setFilters({...filters, sortBy: e.target.value})}
 className="w-full px-3 py-2 text-sm border border-ink-200 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-all"
 >
 <option value="relevance">相关性</option>
 <option value="date">发表时间</option>
 <option value="cited">引用次数</option>
 </select>
 </div>
 <div>
 <label className="block text-xs text-ink-500 mb-1">语言偏好</label>
 <select
 value={filters.language}
 onChange={(e) => setFilters({...filters, language: e.target.value})}
 className="w-full px-3 py-2 text-sm border border-ink-200 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-all"
 >
 <option value="any">任意语言</option>
 <option value="en">英语</option>
 <option value="zh">中文</option>
 </select>
 </div>
 </div>
 </div>

 {/* 高级选项 */}
 <div className="space-y-4">
 <h4 className="text-sm font-medium text-ink-700 flex items-center gap-2">
 <TrendingUp className="w-4 h-4 text-accent-500" />
 高级选项
 </h4>
 <div className="space-y-3">
 <div>
 <label className="block text-xs text-ink-500 mb-1 flex items-center gap-1">
 <Hash className="w-3 h-3" />
 最低引用数
 </label>
 <input
 type="number"
 placeholder="如: 50"
 value={filters.citationMin}
 onChange={(e) => setFilters({...filters, citationMin: e.target.value})}
 className="w-full px-3 py-2 text-sm border border-ink-200 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-all"
 />
 </div>
 <div>
 <label className="block text-xs text-ink-500 mb-1 flex items-center gap-1">
 <UserCheck className="w-3 h-3" />
 作者姓名
 </label>
 <input
 type="text"
 placeholder="如: Zhang"
 value={filters.author}
 onChange={(e) => setFilters({...filters, author: e.target.value})}
 className="w-full px-3 py-2 text-sm border border-ink-200 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-all"
 />
 </div>
 </div>
 </div>
 </div>

 {/* 应用筛选按钮 */}
 <div className="flex items-center justify-between mt-6 pt-4 border-t border-ink-200/50">
 <div className="text-sm text-ink-600">
 {getActiveFiltersCount() > 0 ? (
 <span className="flex items-center gap-1">
 <span className="w-2 h-2 bg-accent-500 rounded-full"></span>
 已设置 {getActiveFiltersCount()} 个筛选条件
 </span>
 ) : (
 '未设置筛选条件'
 )}
 </div>
 <button
 onClick={() => {
 handleSearch();
 setShowFilters(false);
 }}
 className="px-6 py-2 bg-accent-500 text-white text-sm font-medium rounded-lg hover:bg-accent-600 transition-all duration-200 shadow-md hover:shadow-lg"
 >
 应用筛选
 </button>
 </div>
 </div>
 )}
 </div>

 {/* 搜索结果 */}
 {searchResults.length > 0 && (
 <div>
 {/* 结果统计 */}
 <div className="mb-4 text-sm text-ink-600">
 找到约 {totalResults.toLocaleString()} 条结果
 </div>

 {/* 结果列表 */}
 <div className="space-y-4 sm:space-y-6">
 {searchResults.map(renderSearchResult)}
 </div>

 {/* 分页 */}
 <div className="mt-8 flex justify-center">
 <div className="flex items-center gap-2">
 <button
 disabled={currentPage <= 1}
 onClick={() => handleSearch(searchQuery, currentPage - 1)}
 className="px-4 py-2 border border-ink-200 text-ink-600 hover:bg-cream-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
 >
 上一页
 </button>
 <span className="px-4 py-2 text-ink-700">
 第 {currentPage} 页
 </span>
 <button
 onClick={() => handleSearch(searchQuery, currentPage + 1)}
 className="px-4 py-2 border border-ink-200 text-ink-600 hover:bg-cream-100 rounded-md"
 >
 下一页
 </button>
 </div>
 </div>
 </div>
 )}

 {/* 空状态 */}
 {!loading && searchResults.length === 0 && searchQuery && (
 <div className="text-center py-12">
 <BookOpen className="w-16 h-16 text-ink-300 mx-auto mb-4" />
 <h3 className="text-lg font-semibold text-ink-900 mb-2">未找到相关文献</h3>
 <p className="text-ink-600">尝试使用其他关键词或调整筛选条件</p>
 </div>
 )}

 {/* 使用提示 */}
 {!searchQuery && !loading && searchResults.length === 0 && (
 <div className="bg-white rounded-lg border border-ink-200 p-6 sm:p-8">
 <div className="text-center">
 <BookOpen className="w-16 h-16 text-accent-500 mx-auto mb-4" />
 <h3 className="text-xl font-semibold text-ink-900 mb-4">开始您的学术搜索</h3>
 <p className="text-ink-600 mb-6 max-w-2xl mx-auto">
 输入关键词搜索数百万篇学术论文、学位论文、书籍、预印本、摘要和技术报告
 </p>
 
 {/* 搜索建议 */}
 <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
 <div className="text-center">
 <Search className="w-8 h-8 text-accent-500 mx-auto mb-2" />
 <h4 className="font-medium text-ink-900 mb-1">精确搜索</h4>
 <p className="text-sm text-ink-600">使用引号搜索精确短语</p>
 </div>
 <div className="text-center">
 <Filter className="w-8 h-8 text-green-500 mx-auto mb-2" />
 <h4 className="font-medium text-ink-900 mb-1">高级筛选</h4>
 <p className="text-sm text-ink-600">按年份、作者等条件筛选</p>
 </div>
 <div className="text-center">
 <Quote className="w-8 h-8 text-accent-500 mx-auto mb-2" />
 <h4 className="font-medium text-ink-900 mb-1">引用统计</h4>
 <p className="text-sm text-ink-600">查看文献被引用次数</p>
 </div>
 </div>
 </div>
 </div>
 )}
 </div>
 </div>
 );
}